import { useEffect, useState } from 'react';
import { StanButton, StanInput } from '@/components';
import { usePromoCode } from '../hooks/usePromoCode';
import { cn } from '@/lib';
import IcCheck from '@/assets/icons/ic-check.svg';

interface PromoCodeInputProps {
    prefillCode?: string;
    onPromoApplied?: (promoId: string, isFreeSubscription: boolean, discountPercent?: number) => void;
}

export const PromoCodeInput = ({ prefillCode, onPromoApplied }: PromoCodeInputProps) => {
    const [isExpanded, setIsExpanded] = useState(!!prefillCode);
    const { promoCode, setPromoCode, isValidating, validation, validatePromoCode, clearPromoCode } = usePromoCode();

    // Auto-apply prefilled code on mount
    useEffect(() => {
        if (prefillCode) {
            setPromoCode(prefillCode);
            validatePromoCode(prefillCode);
        }
    }, []);

    const handleApply = () => {
        validatePromoCode(promoCode);
    };

    // Notify parent when promo is applied
    useEffect(() => {
        if (validation.isValid && validation.promoId && onPromoApplied) {
            onPromoApplied(validation.promoId, validation.isFreeSubscription ?? false, validation.discountPercent);
        }
    }, [
        validation.isValid,
        validation.promoId,
        validation.isFreeSubscription,
        validation.discountPercent,
        onPromoApplied,
    ]);

    const handleClear = () => {
        clearPromoCode();
        onPromoApplied?.('', false, undefined);
    };

    if (!isExpanded) {
        return (
            <div className='flex justify-end'>
                <StanButton
                    name='promo-code-field-expand-button'
                    type='button'
                    size='sm'
                    onClick={() => setIsExpanded(true)}
                >
                    Have a Promo Code?
                </StanButton>
            </div>
        );
    }

    return (
        <div className='mt-4'>
            <h4 className='text-dark mb-2'>Discount Code</h4>
            <div className='flex flex-col gap-2'>
                <div className='relative flex-1'>
                    <StanInput
                        name='registration-promo-code-input'
                        value={promoCode}
                        onChange={e => setPromoCode(e.target.value)}
                        placeholder='Enter promo code here'
                        disabled={validation.isValid}
                        className={cn(
                            validation.isValid && 'border-success',
                            validation.errorMessage && 'border-negative'
                        )}
                    />
                    {validation.isValid && (
                        <div className='absolute top-1/2 right-3 -translate-y-1/2'>
                            <IcCheck className='text-success size-5' />
                        </div>
                    )}
                </div>
                {validation.isValid ? (
                    <StanButton
                        name='clear-promo-code'
                        type='button'
                        variant='outline'
                        onClick={handleClear}
                        className='shrink-0'
                    >
                        Clear
                    </StanButton>
                ) : (
                    <StanButton
                        name='apply-promo-code'
                        type='button'
                        variant='outline'
                        onClick={handleApply}
                        loading={isValidating}
                        disabled={!promoCode.trim() || isValidating}
                        className='shrink-0'
                    >
                        Apply
                    </StanButton>
                )}
            </div>
            {validation.errorMessage && (
                <p className='text-negative text-caption-1-regular mt-1'>{validation.errorMessage}</p>
            )}
            {validation.isValid && validation.discountPercent && (
                <p className='text-success text-caption-1-regular mt-1'>
                    {validation.discountPercent === 100
                        ? 'Free subscription applied!'
                        : `${validation.discountPercent}% discount applied!`}
                </p>
            )}
        </div>
    );
};
